/**
 *  SankeyComponent script file
 **/
import * as d3 from 'd3'
import {
  sankey as d3Sankey,
  sankeyLinkHorizontal as d3SsankeyLinkHorizontal
} from 'd3-sankey'
import { formatNumber } from '@/utils/formatNumber'
import { normalizeString } from '@/utils/normalizeString'
import { clientMixin } from '../../mixins/fetch'

const uid = () => {
  return 'chart-node--xxxxxxxx'.replace(/[x]/g, function (c) {
    var r = (Math.random() * 16) | 0
    var v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export default {
  name: 'SankeyComponent',
  mixins: [clientMixin],
  components: {},
  props: {
    properties: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      config: this.properties.config,
      colors: this.properties.config.colors,
      spinner:
        (this.properties.config &&
          this.properties.config.colors &&
          this.properties.config.colors.spinner) ||
        {},
      data: {},
      uids: [],
      clicked: false,
      itemSelected: undefined,
      total: 0,
      targets: [],
      selectedTab: this.properties.config.sankey.activeTab,
      uid: null,
      skills: null
    }
  },
  created () {
    this.uid = `component-${this._uid}`
  },
  async mounted () {
    await this.getSkills()

    this.total = this.totalC

    this.$nextTick(function () {
      if (!this.isLoading) {
        this.init()
        this.drawChart()
      }
    })
  },
  computed: {
    values () {
      return this.skills && this.skills.values ? this.skills.values : []
    },
    sourceDate () {
      return this.skills && this.skills.date
    },
    totalC () {
      return formatNumber(
        this.values.reduce((acum, current) => {
          acum += parseFloat(current.value)
          return acum
        }, 0)
      )
    },
    selectSkills () {
      return [...new Set(this.values.map(x => x.source))].map(x => ({
        id: x,
        name: x
      }))
    },
    selectJobs () {
      return [...new Set(this.values.map(x => x.target))]
        .map(x => ({ id: x, name: x }))
        .sort(function (a, b) {
          const x = a.id.toLowerCase()
          const y = b.id.toLowerCase()
          if (x < y) {
            return -1
          }
          if (x > y) {
            return 1
          }
          return 0
        })
    },
    colorsPalette () {
      return d3.scaleOrdinal(this.colors.charts.palette)
    }
  },
  methods: {
    async getSkills () {
      const { data } = this.properties

      if (!Array.isArray(data) || !data.length) {
        return
      }

      try {
        this.isLoading = true

        const [skills] = await Promise.all(
          this.properties.data.map(async file => {
            try {
              return await this.getData(file)
            } catch (error) {
              return null
            }
          })
        )

        this.skills = skills
      } catch (error) {
      } finally {
        this.isLoading = false
      }
    },
    init () {
      this.uids = [...Array(this.values.length)].map(i => `${uid()}`)
      const parsed = this.values.map((s, i) => ({ ...s, uid: this.uids[i] }))
      const names = Array.from(
        new Set(parsed.flatMap(l => [l.source, l.target])),
        name => ({ name })
      )

      this.data = {
        links: parsed.map(p => ({
          ...p,
          value:
            p.value < this.config.sankey.nodeMinValue
              ? this.config.sankey.nodeMinValue
              : p.value,
          real: p.value
        })),
        nodes: this.parseNodes(names, parsed)
      }
    },
    drawChart () {
      d3.select(`#${this.uid} .chart-wrapper .svg`)
        .selectAll('*')
        .remove()

      const wrapper = document
        .querySelector(`#${this.uid} .chart-wrapper`)
        .getBoundingClientRect()

      const getBB = selection => {
        selection.each(function (d) {
          d.bbox = this.getBBox()
        })
      }
      const sankey = d3Sankey()
        .nodeId(d => d.name)
        .nodeWidth(16)
        .nodePadding(2)
        .size([wrapper.width, this.config.sankey.height])

      const { nodes, links } = sankey({
        nodes: this.data.nodes.map(d => ({ ...d })),
        links: this.data.links.map(d => ({ ...d }))
      })

      const svg = d3
        .select(`#${this.uid} svg`)
        .attr('viewBox', [0, 0, wrapper.width, this.config.sankey.height])
        .attr('height', this.config.sankey.height)

      const link = svg
        .append('g')
        .attr('fill', 'none')
        .selectAll(`#${this.uid} g`)
        .data(links)
        .join('g')

      const gradient = link
        .append('linearGradient')
        .attr('id', d => d.uid)
        .attr('gradientUnits', 'userSpaceOnUse')
        .attr('x1', d => d.source.x1)
        .attr('x2', d => d.target.x0)

      gradient
        .append('stop')
        .attr('offset', '0%')
        .attr('stop-color', d => this.colorsPalette(d.source.name))

      gradient
        .append('stop')
        .attr('offset', '100%')
        .attr('stop-color', d => this.colorsPalette(d.target.name))

      link
        .append('path')
        .attr('class', d => `link-path ${d.uid}`)
        .attr('d', d3SsankeyLinkHorizontal())
        .style('stroke', d => `url('#${d.uid}')`)
        .style('stroke-opacity', 0.4)
        .style('stroke-width', d => Math.max(1, d.width))

      const rects = svg
        .append('g')
        .selectAll(`#${this.uid} rect`)
        .data(nodes)
        .join('g')
        .attr('class', d => {
          const targets = d.targetLinks.map(i => i.uid) || []
          const sources = d.sourceLinks.map(i => i.uid) || []
          const classes = [...targets, ...sources].join(' ', ' ')
          return `node ${classes}`
        })
        .attr(
          'id',
          d =>
            normalizeString(d.name)
              .toLowerCase()
              .split(' ')
              .join('-') + '-sankey'
        )
        .on('click', this.onNodeClick)
        .on('mouseover', this.showLinks)
        .on('mouseout', this.hideLinks)

      rects
        .append('rect')
        .attr('class', 'node-name')
        .attr('x', d => d.x0)
        .attr('y', d => d.y0)
        .attr('height', d => d.y1 - d.y0)
        .attr('width', d => d.x1 - d.x0)
        .attr('fill', d => this.colorsPalette(d.name))
        .attr('cursor', 'pointer')

      rects.append('g').style('opacity', 0)

      rects
        .select(`#${this.uid} g`)
        .append('text')
        .attr('class', 'node-text')
        .attr('x', d => (d.x0 < wrapper.width / 2 ? d.x1 + 6 : d.x0 - 6))
        .attr('y', d => (d.y1 + d.y0) / 2)
        .attr('dy', '0.35em')
        .attr('text-anchor', d => (d.x0 < wrapper.width / 2 ? 'start' : 'end'))
        .attr('font-family', this.config.font)
        .attr('font-size', '10px')
        .attr('fill', this.colors.charts.text)
        .attr('font-weight', 'bold')
        .style('pointer-events', 'none')
        .text(d => d.name)
        .call(getBB)

      rects
        .select(`#${this.uid} g`)
        .insert('rect', 'text')
        .attr('x', d => d.bbox.x - 4)
        .attr('y', d => d.bbox.y - 2)
        .attr('width', d => d.bbox.width + 8)
        .attr('height', d => d.bbox.height + 4)
        .style('fill', this.colors.charts.textBackground)
        .style('pointer-events', 'none')
    },
    onNodeClick (node) {
      if (this.itemSelected && this.itemSelected.id === node.name) {
        this.setAll()
      } else {
        this.itemSelected = undefined
        this.showLinks(node)
        this.clicked = true
        this.total = 0

        if (node.sourceLinks.length) {
          this.targets = node.sourceLinks.map(item => {
            item.target.real_list = item.real
            return item.target
          })
          this.selectedTab = 'skills'
          this.$gtm.trackEvent({
            event: 'event_graph', // Event type [default = 'interaction'] (Optional)
            category: 'sankey:' + this.config.mapActiveTag,
            action: 'select job',
            label: node.name,
            value: 1,
            noninteraction: false // Optional
          })
        }

        if (node.targetLinks.length) {
          this.targets = node.targetLinks.map(item => {
            item.source.real_list = item.real
            return item.source
          })
          this.selectedTab = 'jobs'
          this.$gtm.trackEvent({
            event: 'event_graph', // Event type [default = 'interaction'] (Optional)
            category: 'sankey:' + this.config.mapActiveTag,
            action: 'select job skill',
            label: node.name,
            value: 1,
            noninteraction: false // Optional
          })
        }

        this.total = formatNumber(
          this.targets.reduce((acum, current) => {
            acum += parseInt(current.real_list, 10)
            return acum
          }, 0)
        )

        this.targets.forEach(item => {
          item.color = this.colorsPalette(item.name)
        })

        // this.total = formatNumber(this.targets.reduce((acum, current) => {
        //   acum += current.real
        //   return acum
        // }, 0))

        this.itemSelected = {
          id: node.name,
          name: node.name,
          color: this.colorsPalette(node.name)
        }

        this.onDropdownToggle()
      }
    },
    setAll () {
      this.clicked = false
      this.itemSelected = undefined
      this.hideLinks()
    },
    showLinks (node) {
      if (this.itemSelected) {
        return
      }

      this.clicked = false

      d3.selectAll(`#${this.uid} .link-path`)
        .transition()
        .duration(250)
        .ease(d3.easeLinear)
        .style('stroke-opacity', 0)

      d3.selectAll(`#${this.uid} .node`)
        .transition()
        .duration(250)
        .ease(d3.easeLinear)
        .style('opacity', 0.2)

      d3.selectAll(`#${this.uid} .node g`)
        .transition()
        .duration(250)
        .ease(d3.easeLinear)
        .style('opacity', 0)

      const targets = d3
        .selectAll(`#${this.uid} .link-path`)
        .filter(l => node.targetLinks.indexOf(l) !== -1)
      const sources = d3
        .selectAll(`#${this.uid} .link-path`)
        .filter(l => node.sourceLinks.indexOf(l) !== -1)

      node.targetLinks.forEach(l => {
        d3.selectAll(`#${this.uid} .node.${l.uid} `)
          .transition()
          .duration(250)
          .ease(d3.easeLinear)
          .style('opacity', 1)

        d3.selectAll(`#${this.uid} .node.${l.uid} g`)
          .transition()
          .duration(250)
          .ease(d3.easeLinear)
          .style('opacity', 1)
      })

      node.sourceLinks.forEach(l => {
        d3.selectAll(`#${this.uid} .node.${l.uid} `)
          .transition()
          .duration(250)
          .ease(d3.easeLinear)
          .style('opacity', 1)

        d3.selectAll(`#${this.uid} .node.${l.uid} g`)
          .transition()
          .duration(250)
          .ease(d3.easeLinear)
          .style('opacity', 1)
      })

      targets
        .transition()
        .duration(250)
        .ease(d3.easeLinear)
        .style('stroke-opacity', 0.4)

      sources
        .transition()
        .duration(250)
        .ease(d3.easeLinear)
        .style('stroke-opacity', 0.4)
    },
    hideLinks () {
      if (this.clicked) {
        return
      }

      d3.selectAll(`#${this.uid} .link-path`)
        .transition()
        .duration(250)
        .ease(d3.easeLinear)
        .style('stroke-opacity', 0.4)

      d3.selectAll(`#${this.uid} .node`)
        .transition()
        .duration(250)
        .ease(d3.easeLinear)
        .style('opacity', 1)

      d3.selectAll(`#${this.uid} .node g`)
        .transition()
        .duration(250)
        .ease(d3.easeLinear)
        .style('opacity', 0)
    },
    setSelection (node) {
      const str =
        normalizeString(node)
          .toLowerCase()
          .split(' ')
          .join('-') + '-sankey'
      const el = document.querySelector(`#${this.uid} #${str}`)
      el.dispatchEvent(new Event('click'))
      this.$refs.dropdown.close()
      this.$refs.dropdownScrollbar.scrollTo(0, 0)
    },
    onDropdownToggle () {
      this.$nextTick(() => {
        this.$refs.dropdownScrollbar.scrollTo(0, 0)
        this.$refs.dropdownScrollbar.update()
      })
    },
    setActiveTab (tab) {
      this.selectedTab = tab
      this.$nextTick(() => {
        this.$refs.dropdownScrollbar.scrollTo(0, 0)
        this.$refs.dropdownScrollbar.update()
      })
    },
    parseNodes (names, parsed) {
      return names.map(n => {
        const value = parsed
          .filter(p => p.source === n.name || p.target === n.name)
          .reduce((acc, i) => acc + parseFloat(i.value), 0)
        const fake =
          value < this.config.sankey.nodeMinValue
            ? this.config.sankey.nodeMinValue
            : value
        return {
          name: n.name,
          value: fake,
          real: value
        }
      })
    }
  },
  watch: {
    clicked (value) {
      if (!value) {
        this.itemSelected = undefined
      }
    },
    itemSelected (value) {
      if (!value) {
        this.total = this.totalC
        this.targets = []
      }
    }
  }
}
